import { Tooltip } from "bootstrap";
import olMap from 'ol/Map';
import { IMapConfig } from "./MapConfig";
import { MapCore } from "./MapCore";
import { MapControls } from "./MapControls";
import { MapLoadingIndicator } from "./MapLoadingIndicator";
import { BaseLayerController } from "./BaseLayerController";
import { OverlayController } from "./OverlayController";
import { MapNavigator } from "./MapNavigator";
import { MeasureTool } from "./MeasureTool";
import { FeatureInfoController } from "./FeatureInfoController";
import { SearchCoordinator } from "./SearchCoordinator";
import { AuthSessionMonitor } from "./AuthSessionMonitor";
import { MapModeManager } from "./MapModeManager";
import TrailwiseEventBus from '../TrailwiseEventBus';
import { MapModeChangedPayload } from '../TrailwiseEvents';
import { AppStorage } from "../AppStorage";
import { MapViewStore } from "../MapViewStore";
import { MapSlidePanel } from "../MapSidePanel";
import { MapContextMenu } from "../MapContextMenu";
import { MapGazetteer } from "../MapGazetteer";
import { AppGeoLocation } from "../AppGeoLocation";
import { MapGpx } from "../gpx/MapGpx";
import { MapGridRef } from "../MapGridRef";
import { BingSession } from "../BingSession";
import { TwuidSearchService } from "../TwuidSearchService";
import { UsrnSearchService } from "../UsrnSearchService";

/**
 * Composition root for the main map page. Constructs and wires the map
 * modules in dependency order — it has no behaviour of its own.
 */
export class MapApp {

    private _mapStoreID = "trailwise-map";
    private _storage: AppStorage;
    private _mapStore: MapViewStore;
    private _mapSlidePanel: MapSlidePanel;
    private _mapCore: MapCore;
    private _mapControls: MapControls;
    private _loadingIndicator: MapLoadingIndicator;
    private _baseLayers: BaseLayerController;
    private _overlays: OverlayController;
    private _navigator: MapNavigator;
    private _measureTool: MeasureTool;
    private _featureInfo: FeatureInfoController;
    private _searchCoordinator: SearchCoordinator;
    private _authMonitor: AuthSessionMonitor;
    private _contextMenu: MapContextMenu;
    private _mapGazetteer: MapGazetteer;
    private _geoLocation: AppGeoLocation;
    private _mapGpx: MapGpx;
    private _mapGridRef: MapGridRef;
    private _twuidSearchService: TwuidSearchService;
    private _usrnSearchService: UsrnSearchService;
    private _modeManager: MapModeManager;

    get olMap(): olMap {
        return this._mapCore.olMap;
    }

    get modeManager(): MapModeManager {
        return this._modeManager;
    }

    public constructor(
        private _config: IMapConfig,
        bingApiOrSessionKey: string,
        bingSession: BingSession | null
    ) {
        this._modeManager = new MapModeManager();
        this._twuidSearchService = new TwuidSearchService();
        this._usrnSearchService = new UsrnSearchService(this._config.wmsUrl);

        //init tooltips
        let tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl);
        });

        this._storage = new AppStorage();
        this._mapStore = new MapViewStore(this._mapStoreID);
        this._mapSlidePanel = new MapSlidePanel(this._mapStore, true, true);

        this._mapCore = new MapCore(this._config.isMember, this._mapStore);
        const map = this._mapCore.olMap;

        this._loadingIndicator = new MapLoadingIndicator();
        this._mapControls = new MapControls(map, this._mapCore.hasTouch);

        this._baseLayers = new BaseLayerController(
            map,
            this._mapStore,
            this._config.isMember,
            bingSession,
            bingApiOrSessionKey,
            this._config.osmApiKey);
        this._mapCore.onZoomChanged(this._baseLayers.handleZoomChanged);

        this._overlays = new OverlayController(
            map,
            this._config.wmsUrl,
            this._mapStore,
            this._storage,
            this._loadingIndicator,
            this._config.infoClickProw,
            this._config.showHidden,
            () => this._featureInfo.closePopup());

        this._navigator = new MapNavigator(map);
        this._measureTool = new MeasureTool(map);

        this._mapCore.setMapView();

        this._featureInfo = new FeatureInfoController(
            map,
            this._config.wmsUrl,
            this._config.infoClickProw,
            this._config.isAuthenticated,
            this._config.isMember,
            this._config.loginUrl,
            this._config.registerUrl,
            this._config.siteId);

        this._contextMenu = new MapContextMenu(map, undefined, this._config.isTitheMapsWales);

        this.initToolRadios();

        this._authMonitor = new AuthSessionMonitor(this._config.isAuthenticated);

        this._mapGazetteer = new MapGazetteer(map, this._twuidSearchService, this._usrnSearchService, this._mapStore);
        this._geoLocation = new AppGeoLocation(map, this._baseLayers.osmContouredLayer);

        this._searchCoordinator = new SearchCoordinator(
            this._mapStore,
            this._navigator,
            this._overlays,
            this._featureInfo,
            this._twuidSearchService,
            this._config.focusConfig);

        this._mapGpx = new MapGpx(map, this._modeManager, this._mapSlidePanel, this._config.canEditGpx);
        this._featureInfo.setPreClickHandler(this._mapGpx.handleMapClick);
        this._mapGridRef = new MapGridRef(map, this._contextMenu, this._mapStore);

        this.wireModeSubscriptions();

        this._searchCoordinator.applyInitialFocus();
    }

    /**
     * Central reactions to map mode transitions. Modules that need richer
     * behaviour subscribe to the bus themselves; the simple show/hide and
     * enable/disable wiring lives here.
     */
    private wireModeSubscriptions = (): void => {
        const self = this;

        TrailwiseEventBus.mapModeChanged.on(function (payload: MapModeChangedPayload) {
            const { from, to } = payload;

            //measure tool only runs in measure mode
            if (from === 'measure' && to !== 'measure') {
                self._measureTool.deactivate();
                self.setToolRadioToRouteInfo();
            }

            //context menu and feature info are for browsing, not measuring/editing
            const browsing = (to === 'idle' || to === 'tracking');
            self._featureInfo.setEnabled(browsing);
            if (browsing) {
                self._contextMenu.activate();
            } else {
                self._contextMenu.deactivate();
            }

            //editing owns the whole map surface
            if (to === 'editing') {
                self._mapSlidePanel.hideAll();
                self._geoLocation.setButtonsVisible(false);
                //route-context layers must be on while editing: force unclassified
                //roads + byways on via the persisted path (panel stays in sync). They
                //are not restored on exit — once on, they stay on.
                self._overlays.ensureVisible(['UCRLines', 'BOATLines']);
            } else if (from === 'editing') {
                self._mapSlidePanel.restore();
                self._geoLocation.setButtonsVisible(true);
            }

            //entering any other mode from tracking ends the tracking session
            if (from === 'tracking' && to !== 'tracking') {
                self._geoLocation.stopTracking();
            }
        });

        //AppGeoLocation raises tracking events from its own buttons — mirror
        //them into the mode machine so it stays authoritative.
        TrailwiseEventBus.trackingStart.on(function () {
            self._modeManager.requestMode('tracking');
        });
        TrailwiseEventBus.trackingEnd.on(function () {
            if (self._modeManager.current === 'tracking') {
                self._modeManager.requestMode('idle');
            }
        });
    }

    private initToolRadios = (): void => {
        const self = this;

        let toolRadios = document.getElementsByName("currentTool");
        for (let i = 0; i < toolRadios.length; i++) {
            toolRadios[i].addEventListener("click", function (this: HTMLElement, ev: MouseEvent) {
                self.toolRadioClicked(this);
            });
        }

        let currentMapState = this._mapStore.getMapInfo();
        if (currentMapState.currentTool) {
            for (let i = 0; i < toolRadios.length; i++) {
                if ((<HTMLInputElement>toolRadios[i]).value === currentMapState.currentTool) {
                    toolRadios[i].click();
                }
            }
        }
    }

    private toolRadioClicked = async (ele: HTMLElement): Promise<void> => {
        const rdoVal = (<HTMLInputElement>ele).value;
        let accepted: boolean;

        switch (rdoVal) {
            case "measureKm":
                accepted = await this._modeManager.requestMode('measure');
                if (accepted) {
                    this._measureTool.activate(true);
                }
                break;

            case "measureMiles":
                accepted = await this._modeManager.requestMode('measure');
                if (accepted) {
                    this._measureTool.activate(false);
                }
                break;

            default:
                accepted = await this._modeManager.requestMode('idle');
                if (accepted) {
                    this._measureTool.deactivate();
                }
        }

        if (accepted) {
            this._mapStore.onCurrentToolChanged(rdoVal);
        } else {
            //the mode change was rejected (e.g. while editing) — revert the radio
            //selection to the persisted tool so the UI and stored state stay in sync
            this.syncToolRadiosToStore();
        }
    }

    private syncToolRadiosToStore = (): void => {
        const current = this._mapStore.getMapInfo().currentTool;
        const radios = document.getElementsByName("currentTool");
        for (let i = 0; i < radios.length; i++) {
            const radio = <HTMLInputElement>radios[i];
            radio.checked = (radio.value === current);
        }
    }

    private setToolRadioToRouteInfo = (): void => {
        const radios = document.getElementsByName("currentTool");
        for (let i = 0; i < radios.length; i++) {
            const radio = <HTMLInputElement>radios[i];
            radio.checked = (radio.value === "route");
        }
        this._mapStore.onCurrentToolChanged("route");
    }
}
